import React, { useEffect } from 'react';
import { Sparkles, ChevronRight, Star, ShoppingCart, Smartphone, TrendingUp, History } from 'lucide-react';
import { usePersonalizedRecommendations, trackProductView, trackProductClick, Product } from './PersonalizedRecommendations';
interface ToysUpsellProps {
  minimal?: boolean;
  style?: 'banner' | 'card' | 'sidebar';
}
export const ToysUpsell: React.FC<ToysUpsellProps> = ({
  minimal = false,
  style = 'banner'
}) => {
  const toys = [{
    id: 'toy1',
    name: 'UltraFeel Smart Sleeve',
    description: 'Interactive sleeve with app control & realistic sensations',
    price: 89.99,
    discount: 15,
    rating: 4.9,
    reviews: 512,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/atVzhj2Ea7Jw4yMSqxK4XM/image.png',
    category: 'sleeve',
    tags: ['interactive', 'app-controlled', 'realistic', 'premium', 'bestseller'],
    bestseller: true,
    connected: true
  }, {
    id: 'toy2',
    name: 'LuxeLight Premium Flashlight',
    description: 'Premium materials with multi-speed vibration patterns',
    price: 79.99,
    discount: 0,
    rating: 4.7,
    reviews: 324,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/shw6CY5v79w8bJBgSSQwod/image.png',
    category: 'flashlight',
    tags: ['premium', 'vibration', 'multi-speed', 'luxury']
  }, {
    id: 'toy3',
    name: 'TightGrip Sleeve Pro',
    description: 'Adjustable grip with warming function',
    price: 69.99,
    discount: 10,
    rating: 4.5,
    reviews: 186,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6gymBp7TU8GMLLGvrhyYDY/image.png',
    category: 'sleeve',
    tags: ['adjustable', 'warming', 'grip', 'comfort']
  }, {
    id: 'toy4',
    name: 'PocketJoy Flashlight',
    description: 'Compact design with realistic texture',
    price: 59.99,
    discount: 5,
    rating: 4.6,
    reviews: 248,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6gymBp7TU8GMLLGvrhyYDY/image.png',
    category: 'flashlight',
    tags: ['compact', 'realistic', 'texture', 'portable'],
    connected: true
  }, {
    id: 'toy5',
    name: 'VibrationMax Pro',
    description: 'Advanced vibration technology with 12 patterns and 5 speeds',
    price: 99.99,
    discount: 20,
    rating: 4.8,
    reviews: 412,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6gymBp7TU8GMLLGvrhyYDY/image.png',
    category: 'vibrator',
    tags: ['vibration', 'patterns', 'speeds', 'advanced', 'premium'],
    connected: true
  }, {
    id: 'toy6',
    name: 'AutoStroke Elite',
    description: 'Hands-free experience with automatic motion and adjustable speed',
    price: 129.99,
    discount: 10,
    rating: 4.7,
    reviews: 286,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6gymBp7TU8GMLLGvrhyYDY/image.png',
    category: 'automatic',
    tags: ['automatic', 'hands-free', 'adjustable', 'premium', 'luxury'],
    connected: true
  }];
  // Get personalized recommendations
  const {
    items: recommendedToys,
    reasons,
    loading
  } = usePersonalizedRecommendations(toys, 4);
  // Track component view
  useEffect(() => {
    trackProductView('toys_section', 'toys');
  }, []);
  // Handle product click
  const handleProductClick = (productId: string) => {
    trackProductClick(productId);
  };
  if (minimal) {
    // For minimal view, pick the top recommended toy
    const topToy = recommendedToys[0] || toys[0];
    return <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl border border-purple-800/50 p-3 flex items-center justify-between" data-id="element-21478">
        <div className="flex items-center gap-2" data-id="element-21479">
          <Smartphone className="text-purple-400" size={18} data-id="element-21480" />
          <div data-id="element-21481">
            <h4 className="text-sm font-medium" data-id="element-21482">Interactive Toys</h4>
            <p className="text-xs text-gray-400" data-id="element-21483">
              {loading ? 'Finding the best for you...' : reasons[topToy.id] || 'Connect with your angels in real-time'}
            </p>
          </div>
        </div>
        <button className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-xs text-white" onClick={() => handleProductClick(topToy.id)} data-id="element-21484">
          Shop Now
        </button>
      </div>;
  }
  if (style === 'sidebar') {
    // Use top 2 recommended toys for sidebar
    const sidebarToys = loading ? toys.slice(0, 2) : recommendedToys.slice(0, 2);
    return <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-xl border border-purple-800/40 p-3" data-id="element-21485">
        <div className="flex items-center justify-between mb-2" data-id="element-21486">
          <h3 className="font-medium text-sm" data-id="element-21487">Interactive Toys</h3>
          <button className="text-xs text-purple-400 flex items-center" data-id="element-21488">
            View All
            <ChevronRight size={14} data-id="element-21489" />
          </button>
        </div>
        <div className="space-y-2" data-id="element-21490">
          {sidebarToys.map(toy => <div key={toy.id} className="bg-gray-900 rounded-lg p-2 flex gap-2 cursor-pointer" onClick={() => handleProductClick(toy.id)} data-id="element-21491">
              <div className="w-12 h-12 bg-gray-800 rounded-md overflow-hidden flex-shrink-0" data-id="element-21492">
                <img src={toy.image} alt={toy.name} className="w-full h-full object-cover" data-id="element-21493" />
              </div>
              <div className="flex-1" data-id="element-21494">
                <div className="flex items-center gap-1" data-id="element-21495">
                  <h4 className="text-xs font-medium line-clamp-1" data-id="element-21496">
                    {toy.name}
                  </h4>
                  {toy.connected && <Smartphone size={10} className="text-purple-400" data-id="element-21497" />}
                </div>
                <div className="flex items-center gap-1 mt-0.5" data-id="element-21498">
                  <div className="flex items-center" data-id="element-21499">
                    <Star size={10} className="text-yellow-400 fill-yellow-400" data-id="element-21500" />
                    <span className="text-[10px] ml-0.5" data-id="element-21501">{toy.rating}</span>
                  </div>
                  <span className="text-[10px] text-gray-500" data-id="element-21502">•</span>
                  <span className="text-[10px] text-gray-400" data-id="element-21503">
                    {toy.reviews} reviews
                  </span>
                </div>
                {!loading && reasons[toy.id] && <div className="text-[10px] text-purple-400 flex items-center gap-0.5 mt-0.5" data-id="element-21504">
                    <TrendingUp size={8} data-id="element-21505" />
                    <span className="line-clamp-1" data-id="element-21506">{reasons[toy.id]}</span>
                  </div>}
                <div className="flex items-center justify-between mt-1" data-id="element-21507">
                  <div className="flex items-baseline gap-1" data-id="element-21508">
                    <span className="text-xs font-bold" data-id="element-21509">${toy.price}</span>
                    {toy.discount > 0 && <span className="text-[10px] text-gray-400 line-through" data-id="element-21510">
                        ${toy.price * (100 + toy.discount) / 100}
                      </span>}
                  </div>
                  <button className="text-[10px] px-1.5 py-0.5 bg-purple-600 rounded text-white" data-id="element-21511">
                    Add
                  </button>
                </div>
              </div>
            </div>)}
        </div>
      </div>;
  }
  if (style === 'card') {
    // Use top recommended toy for card
    const featured = loading ? toys.find(t => t.bestseller) || toys[0] : recommendedToys[0];
    return <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl border border-purple-800/50 overflow-hidden" data-id="element-21512">
        <div className="p-2" data-id="element-21513">
          <div className="flex items-center justify-between mb-1" data-id="element-21514">
            <h3 className="font-medium text-sm" data-id="element-21515">Interactive Toys</h3>
            {featured.discount > 0 && <div className="text-xs px-2 py-0.5 bg-purple-500/20 rounded-full text-purple-400" data-id="element-21516">
                {featured.discount}% OFF
              </div>}
          </div>
          <div className="grid grid-cols-4 gap-2" data-id="element-21517">
            <div className="col-span-3 flex flex-col justify-between" data-id="element-21518">
              <div data-id="element-21519">
                <div className="flex items-center gap-1" data-id="element-21520">
                  <p className="text-sm font-medium" data-id="element-21521">{featured.name}</p>
                  {featured.connected && <Smartphone size={12} className="text-purple-400" data-id="element-21522" />}
                </div>
                <div className="flex items-center gap-1 mt-0.5" data-id="element-21523">
                  <Star size={12} className="text-yellow-400" data-id="element-21524" />
                  <span className="text-xs" data-id="element-21525">{featured.rating}</span>
                  <span className="text-xs text-gray-400" data-id="element-21526">•</span>
                  <span className="text-xs text-gray-400" data-id="element-21527">
                    {featured.reviews} reviews
                  </span>
                </div>
                {!loading && reasons[featured.id] && <div className="text-xs text-purple-400 flex items-center gap-1 mt-0.5" data-id="element-21528">
                    <TrendingUp size={10} data-id="element-21529" />
                    <span className="truncate" data-id="element-21530">{reasons[featured.id]}</span>
                  </div>}
              </div>
              <div className="flex items-center justify-between mt-1" data-id="element-21531">
                <div className="flex items-baseline gap-1" data-id="element-21532">
                  <span className="text-base font-bold" data-id="element-21533">${featured.price}</span>
                  {featured.discount > 0 && <span className="text-xs text-gray-400 line-through" data-id="element-21534">
                      ${featured.price * (100 + featured.discount) / 100}
                    </span>}
                </div>
                <button className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-white text-xs font-medium" onClick={() => handleProductClick(featured.id)} data-id="element-21535">
                  Shop Now
                </button>
              </div>
            </div>
            <div className="relative h-20 rounded-lg overflow-hidden" data-id="element-21536">
              <img src={featured.image} alt={featured.name} className="w-full h-full object-cover" data-id="element-21537" />
              {featured.bestseller && <div className="absolute top-1 left-1 px-1 py-0.5 bg-yellow-500 rounded text-[10px] font-medium text-black" data-id="element-21538">
                  BEST
                </div>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Default banner style with personalized recommendations
  const displayToys = loading ? toys : recommendedToys;
  return <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl border border-purple-800/50 p-4" data-id="element-21539">
      <div className="flex items-center justify-between mb-3" data-id="element-21540">
        <div className="flex items-center gap-2" data-id="element-21541">
          <div className="w-10 h-10 flex items-center justify-center bg-purple-500/20 rounded-full text-purple-400" data-id="element-21542">
            <Sparkles size={20} data-id="element-21543" />
          </div>
          <div data-id="element-21544">
            <h3 className="font-medium" data-id="element-21545">Interactive Toys</h3>
            <p className="text-sm text-gray-300" data-id="element-21546">
              {loading ? 'Personalizing recommendations...' : 'Tailored to your preferences'}
            </p>
          </div>
        </div>
        <div className="text-xs px-2 py-0.5 bg-purple-500/20 rounded-full text-purple-400" data-id="element-21547">
          {loading ? 'TRENDING' : 'FOR YOU'}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2" data-id="element-21548">
        {displayToys.map(toy => <div key={toy.id} className="bg-gray-900 rounded-lg p-2 flex flex-col cursor-pointer" onClick={() => handleProductClick(toy.id)} data-id="element-21549">
            <div className="relative w-full aspect-square bg-gray-800 rounded-md overflow-hidden mb-2" data-id="element-21550">
              <img src={toy.image} alt={toy.name} className="w-full h-full object-cover" data-id="element-21551" />
              {toy.discount > 0 && <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-red-500 rounded text-xs font-medium text-white" data-id="element-21552">
                  {toy.discount}% OFF
                </div>}
              {toy.connected && <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-purple-500/70 rounded text-[10px] font-medium text-white flex items-center gap-0.5" data-id="element-21553">
                  <Smartphone size={10} data-id="element-21554" />
                  <span data-id="element-21555">APP</span>
                </div>}
              {!loading && reasons[toy.id] && <div className="absolute top-1 left-1 right-1 px-1.5 py-0.5 bg-purple-500/70 backdrop-blur-sm rounded text-[10px] font-medium text-white flex items-center gap-0.5" data-id="element-21556">
                  <TrendingUp size={10} data-id="element-21557" />
                  <span className="line-clamp-1" data-id="element-21558">{reasons[toy.id]}</span>
                </div>}
            </div>
            <h4 className="text-xs font-medium line-clamp-1" data-id="element-21559">{toy.name}</h4>
            <div className="flex items-center gap-1 mt-1" data-id="element-21560">
              <Star size={12} className="text-yellow-400 fill-yellow-400" data-id="element-21561" />
              <span className="text-xs" data-id="element-21562">{toy.rating}</span>
            </div>
            <div className="flex items-center justify-between mt-auto pt-2" data-id="element-21563">
              <span className="font-bold" data-id="element-21564">${toy.price}</span>
              <button className="p-1.5 bg-purple-600 rounded text-white" data-id="element-21565">
                <ShoppingCart size={14} data-id="element-21566" />
              </button>
            </div>
          </div>)}
      </div>
      <button className="w-full mt-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-white text-sm font-medium" onClick={() => trackProductClick('all_toys')} data-id="element-21567">
        Browse All Interactive Toys
      </button>
    </div>;
};